import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import ImageUploader from '../components/ImageUploader';

const HeroManager = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlide, setEditingSlide] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const initialForm = {
    bg: '',
    badge: '',
    title: '',
    highlight: '',
    desc: '',
    cta1: '',
    cta2: '',
    link1: '',
    link2: '',
    order: 0
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const res = await api.get('/hero');
      if (res.data.success) {
        setSlides(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlide) {
        await api.put(`/hero/${editingSlide._id}`, formData);
        alert('Slide updated successfully');
      } else {
        await api.post('/hero', formData);
        alert('Slide added successfully');
      }
      setEditingSlide(null);
      setIsAdding(false);
      setFormData(initialForm);
      fetchSlides();
    } catch (err) {
      console.error(err);
      alert('Failed to save slide');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;
    try {
      await api.delete(`/hero/${id}`);
      fetchSlides();
    } catch (err) {
      console.error(err);
      alert('Failed to delete slide');
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData(slide);
    setIsAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-[#2A2858] font-serif">Hero Slider Manager</h2>
          <p className="text-stone-500 mt-1">Manage dynamic content for the homepage hero section</p>
        </div>
        <button 
          onClick={() => {
            setIsAdding(!isAdding);
            setEditingSlide(null);
            setFormData(initialForm);
          }}
          className="bg-[#2A2858] text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-[#14122B] transition-all shadow-lg"
        >
          <span className="material-symbols-outlined">{isAdding ? 'close' : 'add'}</span>
          {isAdding ? 'Cancel' : 'Add New Slide'}
        </button>
      </div>

      {isAdding && (
        <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#E8B84B]/20 mb-12 animate-fade-in">
          <h3 className="text-xl font-bold text-[#2A2858] mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined">edit_square</span>
            {editingSlide ? 'Edit Slide' : 'Create New Slide'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-4">Background Image</label>
              <div className="flex flex-col md:flex-row gap-6 items-start bg-stone-50 p-6 rounded-2xl border border-stone-100">
                {formData.bg ? (
                  <div className="w-full md:w-64 aspect-video rounded-xl overflow-hidden border-2 border-[#E8B84B]/30 shadow-md relative group">
                    <img src={formData.bg} alt="Preview" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                       <span className="text-white text-[10px] font-bold uppercase tracking-widest">Current Image</span>
                    </div>
                  </div>
                ) : (
                  <div className="w-full md:w-64 aspect-video rounded-xl border-2 border-dashed border-stone-200 flex flex-col items-center justify-center bg-white">
                    <span className="material-symbols-outlined text-stone-200 text-4xl mb-2">image</span>
                    <span className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">No Image Selected</span>
                  </div>
                )}
                <div className="flex-1 space-y-4">
                  <ImageUploader 
                    buttonText={formData.bg ? "Change Background Image" : "Upload Background Image"}
                    onUploadSuccess={(url) => setFormData({...formData, bg: url})}
                  />
                  <div className="space-y-1">
                    <p className="text-[11px] text-stone-500 font-medium">Recommended: 1920x1080px (16:9)</p>
                    <p className="text-[10px] text-stone-400 italic flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px]">info</span>
                      Cloudinary automatically optimizes and serves your image via CDN
                    </p>
                  </div>
                </div>
              </div>
              <input type="hidden" required value={formData.bg} />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Badge Text</label>
              <input 
                type="text" 
                required
                value={formData.badge}
                onChange={e => setFormData({...formData, badge: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="Trusted by 50,000+..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Main Title</label>
              <input 
                type="text" 
                required
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="Connect with your..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Highlight Title (Italic/Gold)</label>
              <input 
                type="text" 
                required
                value={formData.highlight}
                onChange={e => setFormData({...formData, highlight: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="Spiritual Roots"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Order (Sorting)</label>
              <input 
                type="number" 
                value={formData.order}
                onChange={e => setFormData({...formData, order: parseInt(e.target.value)})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Description</label>
              <textarea 
                required
                value={formData.desc}
                onChange={e => setFormData({...formData, desc: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none h-24"
                placeholder="Brief description of the slide..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Button 1 Text</label>
              <input 
                type="text" 
                required
                value={formData.cta1}
                onChange={e => setFormData({...formData, cta1: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="Book now"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Button 1 Link</label>
              <input 
                type="text" 
                required
                value={formData.link1}
                onChange={e => setFormData({...formData, link1: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="/consultations or https://..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Button 2 Text</label>
              <input 
                type="text" 
                required
                value={formData.cta2}
                onChange={e => setFormData({...formData, cta2: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="Explore"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Button 2 Link</label>
              <input 
                type="text" 
                required
                value={formData.link2}
                onChange={e => setFormData({...formData, link2: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="/centre-services or https://..."
              />
            </div>
            <div className="md:col-span-2 flex justify-end gap-4 mt-4">
              <button 
                type="button"
                onClick={() => { setIsAdding(false); setEditingSlide(null); }}
                className="px-8 py-3 rounded-xl font-bold text-stone-500 hover:bg-stone-100 transition-all"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="bg-[#E8B84B] text-white px-10 py-3 rounded-xl font-bold hover:bg-[#B8860B] transition-all shadow-lg"
              >
                {editingSlide ? 'Update Slide' : 'Add Slide'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-12 h-12 border-4 border-[#2A2858]/20 border-t-[#2A2858] rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {slides.map((slide) => (
            <div key={slide._id} className="bg-white rounded-3xl overflow-hidden shadow-md border border-stone-100 group">
              <div className="h-48 relative">
                <img src={slide.bg} alt={slide.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                  <button 
                    onClick={() => handleEdit(slide)}
                    className="w-12 h-12 rounded-full bg-white text-[#2A2858] flex items-center justify-center hover:scale-110 transition-transform"
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                  <button 
                    onClick={() => handleDelete(slide._id)}
                    className="w-12 h-12 rounded-full bg-white text-red-600 flex items-center justify-center hover:scale-110 transition-transform"
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
                <div className="absolute top-4 left-4 bg-[#2A2858] text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-widest">
                  Order: {slide.order}
                </div>
              </div>
              <div className="p-6">
                <div className="text-[10px] text-[#E8B84B] font-bold uppercase tracking-widest mb-2">{slide.badge}</div>
                <h4 className="text-xl font-bold text-stone-800 mb-2 font-serif">{slide.title} <span className="italic text-[#2A2858]">{slide.highlight}</span></h4>
                <p className="text-sm text-stone-500 line-clamp-2 mb-4">{slide.desc}</p>
                <div className="flex gap-2">
                  <span className="text-[10px] bg-stone-100 px-2 py-1 rounded text-stone-500 uppercase font-bold">{slide.cta1}</span>
                  <span className="text-[10px] bg-stone-100 px-2 py-1 rounded text-stone-500 uppercase font-bold">{slide.cta2}</span>
                </div>
              </div>
            </div>
          ))}
          {slides.length === 0 && (
            <div className="lg:col-span-2 py-20 text-center bg-white rounded-3xl border border-dashed border-stone-300">
              <span className="material-symbols-outlined text-5xl text-stone-200 mb-4">view_carousel</span>
              <p className="text-stone-400 font-bold">No slides found. Add your first slide to get started.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeroManager;
